import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { ShieldCheck, Award, CheckCircle, ArrowRight, FileCheck, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const ISO_CERTIFICATE_URL = "https://customer-assets.emergentagent.com/job_ima-montaz-preview/artifacts/6y9y5ari_ISO%20IMA%20MONTAZ.jpg";
const ISO_BADGE_URL = "https://customer-assets.emergentagent.com/job_ima-montaz-preview/artifacts/xjkxagw1_ISO_.png";

const CertificationPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="certification-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#001582] to-[#001ea7] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFC107] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#FFC107] rounded-full" />
              <span className="text-white text-sm font-semibold uppercase tracking-wider">Sertifikasi</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Standar <span className="text-[#FFC107]">Kualitas</span> Internasional
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              Komitmen kami dalam menjaga standar kualitas melalui sistem manajemen mutu 
              yang terstandarisasi dan tersertifikasi internasional.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ISO Certificate Display - Full Width */}
      <section className="py-20 md:py-32 bg-[#f5f7fb]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
              Sertifikat ISO 9001:2015
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              PT IMA MONTAZ TEKNINDO telah berhasil memperoleh sertifikasi ISO 9001:2015 
              untuk sistem manajemen mutu.
            </p>
          </motion.div>

          {/* Certificate Image - Large Display */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center"
          >
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 max-w-4xl w-full">
              <img 
                src={ISO_CERTIFICATE_URL}
                alt="ISO 9001:2015 Certificate - PT IMA MONTAZ TEKNINDO"
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Certificate Details */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* ISO Badge */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex justify-center"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#001582]/10 to-[#FFC107]/10 rounded-full scale-150 blur-3xl" />
                <div className="relative bg-white rounded-3xl shadow-2xl p-10 border border-slate-100" style={{ boxShadow: '0 0 40px rgba(0, 21, 130, 0.1)' }}>
                  <img 
                    src={ISO_BADGE_URL}
                    alt="ISO 9001:2015 Badge"
                    className="w-48 md:w-64 h-auto mx-auto"
                  />
                  <div className="text-center mt-6">
                    <p className="font-heading font-bold text-2xl text-[#001582]">ISO 9001:2015</p>
                    <p className="text-slate-500 mt-1">Quality Management System</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Certificate Details */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6">
                Detail Sertifikasi
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Sertifikasi ISO 9001:2015 membuktikan komitmen PT IMA MONTAZ TEKNINDO dalam 
                menerapkan sistem manajemen mutu yang konsisten dan terstandarisasi untuk 
                semua produk dan layanan kami.
              </p>

              {/* Certificate Info Card */}
              <div className="bg-[#f5f7fb] rounded-2xl p-6 mb-8">
                <h4 className="font-semibold text-[#1a1a1a] mb-4 flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-[#FFC107]" />
                  Informasi Sertifikat
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-slate-200">
                    <span className="text-slate-600">Nomor Sertifikat</span>
                    <span className="font-medium text-[#1a1a1a]">IMTQ01-CCCQ03</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-200">
                    <span className="text-slate-600">Tanggal Sertifikasi Awal</span>
                    <span className="font-medium text-[#1a1a1a]">1 August 2022</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-200">
                    <span className="text-slate-600">Tanggal Sertifikasi</span>
                    <span className="font-medium text-[#1a1a1a]">21 August 2025</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-200">
                    <span className="text-slate-600">Masa Berlaku</span>
                    <span className="font-medium text-[#1a1a1a]">1 August 2028</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-slate-600">Lembaga Sertifikasi</span>
                    <span className="font-medium text-[#1a1a1a]">Equal Assurance</span>
                  </div>
                </div>
              </div>

              {/* Scope */}
              <div className="bg-gradient-to-r from-[#001582] to-[#001ea7] rounded-2xl p-6 text-white">
                <h4 className="font-semibold mb-3">Lingkup Sertifikasi</h4>
                <p className="text-white/90 text-sm leading-relaxed">
                  Supply, manufacture, repair of mechanical electrical equipment parts and 
                  fabricated metal product for oil, gas and industrial.
                </p>
              </div>

              {/* Accreditation */}
              <div className="flex items-center gap-4 mt-8">
                <div className="px-4 py-2 bg-[#001582]/10 rounded-xl">
                  <p className="text-xs text-slate-500">Accredited by</p>
                  <p className="font-semibold text-[#001582]">JAS-ANZ</p>
                </div>
                <div className="px-4 py-2 bg-[#001582]/10 rounded-xl">
                  <p className="text-xs text-slate-500">Member of</p>
                  <p className="font-semibold text-[#001582]">IAF</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quality Commitment */}
      <section className="py-20 bg-[#f5f7fb]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
              Komitmen <span className="text-[#001582]">Kualitas</span>
            </h2>
            <p className="text-slate-600 text-lg">
              Nilai-nilai yang kami pegang teguh dalam menjaga kualitas.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: 'Quality Assurance',
                description: 'Sistem jaminan kualitas yang terintegrasi dalam setiap proses produksi'
              },
              {
                icon: CheckCircle,
                title: 'Continuous Improvement',
                description: 'Komitmen untuk terus meningkatkan kualitas produk dan layanan'
              },
              {
                icon: Award,
                title: 'Customer Satisfaction',
                description: 'Fokus pada kepuasan pelanggan sebagai prioritas utama'
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 text-center shadow-sm border border-slate-100"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-[#001582] to-[#001ea7] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-[#FFC107]" />
                </div>
                <h3 className="font-heading font-bold text-xl text-[#1a1a1a] mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#001582] to-[#001ea7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-6">
            Percayakan kebutuhan industri Anda kepada kami
          </h3>
          <Link to="/kontak">
            <Button 
              size="lg"
              className="bg-[#FFC107] text-[#001582] hover:bg-[#FFD54F] font-bold px-8 py-6 rounded-xl"
            >
              Hubungi Kami
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CertificationPage;
